// The rules every mode shares: what gets drawn, what it scores, and when the
// AUS BONUS fires.
//
// Pure functions only. Nothing here touches Vue, storage or the router — which
// is what makes the scoring testable and what stops six mode screens each
// inventing their own idea of what a streak is worth.
//
// Turning a question into the one-tap shape Endurance and the Daily need lives
// in quickfire.js, so the dependency runs one way: that file needs these rules,
// this file needs nothing from it.

import { BANK } from './bank/index.js';
import { ALL_TOPIC_IDS } from './topics.js';

// --- the AUS BONUS ----------------------------------------------------------
//
// The single best moment in the game, so it must never fire on a near miss.
// It fires when AUSTRALIA or AUSTRIA is the CORRECT answer — not when it is
// merely one of the options, and not when he picks it and is wrong.
//
// The word boundary matters: without it "Australopithecus" would set it off,
// and so would any answer containing "Austria" as a substring of something
// else. Austin's own name is deliberately NOT in here — the joke is that his
// name hides in other words, and a bonus that fired on "Austin" would be the
// app congratulating itself.
const AUS_PATTERN = /\b(australia|australian|austria|austrian)\b/i;

/** Does this answer have Austin's name on it? */
export function isAusAnswer(question, correctLabel) {
  if (question?.ausBonus === true) return true;
  return AUS_PATTERN.test(String(correctLabel ?? ''));
}

// --- difficulty -------------------------------------------------------------

export const DIFFICULTY_ORDER = ['stretching', 'warm-up', 'sweaty', 'fully-austinated'];

// Stretching is for a five-to-eight-year-old, and it is the one rung that is a
// CEILING rather than a floor.
//
// Every other rung means "start here and keep climbing", which is right for an
// eleven-year-old: a question above his level is a thing to have a go at. For a
// five-year-old it is just a question he cannot read. Letting the floor
// semantics apply here would have handed a small child the whole bank with the
// easy questions merely at the bottom of it, which is not an easy mode — it is
// the same mode with a gentler first question.
export const CEILING_DIFFICULTIES = new Set(['stretching']);

/** The rung he chose, and everything harder — unless the rung is a ceiling. */
export function difficultiesAtOrAbove(floor) {
  if (CEILING_DIFFICULTIES.has(floor)) return [floor];
  const i = Math.max(0, DIFFICULTY_ORDER.indexOf(floor));
  return DIFFICULTY_ORDER.slice(i);
}

// Stretching scores less than warm-up on purpose. Everything feeds one shared
// total, so an easier rung that paid the same would make the easy questions the
// efficient way to farm it.
const BASE_POINTS = { stretching: 50, 'warm-up': 100, sweaty: 150, 'fully-austinated': 250 };

// --- drawing questions ------------------------------------------------------

export function shuffle(list) {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Questions for one mode, filtered to his topics and difficulty floor.
 *
 * FALLING BACK RATHER THAN FAILING is the whole point of the two widening
 * steps. He can switch every topic off but one and pick the hardest rung, and
 * a strict filter would then hand a mode two questions and a game that ends in
 * six seconds. Better to quietly widen than to present an empty round — he
 * cannot tell the difference between "no questions match" and "this app is
 * broken", and neither reads as his own filter choice.
 */
export function drawQuestions(mode, { topics = ALL_TOPIC_IDS, difficulty = 'warm-up', count = 20 } = {}) {
  const pool = BANK[mode] ?? [];
  if (!pool.length) return [];

  const allowedTopics = topics?.length ? topics : ALL_TOPIC_IDS;
  const allowedDiff = difficultiesAtOrAbove(difficulty);

  let matching = pool.filter(
    (q) => allowedTopics.includes(q.topic) && allowedDiff.includes(q.difficulty),
  );

  // Widen on difficulty first — he asked for these topics explicitly, and the
  // rung is a floor rather than a demand.
  //
  // NOT on a ceiling rung. Widening there would quietly undo the whole point
  // and hand a five-year-old fully-austinated questions the moment their topic
  // choice ran thin. On a ceiling we drop the TOPIC filter instead, and if that
  // still is not enough, we hand back what little there is.
  const ceiling = CEILING_DIFFICULTIES.has(difficulty);
  if (matching.length < 5 && !ceiling) {
    matching = pool.filter((q) => allowedTopics.includes(q.topic));
  }
  if (matching.length < 5 && ceiling) {
    matching = pool.filter((q) => allowedDiff.includes(q.difficulty));
  }
  // Then give up on the topic filter too, rather than hand back nothing.
  if (matching.length < 5 && !ceiling) matching = pool;

  const bag = shuffle(matching);
  // Loop the bag when a mode is asked for more questions than it has, so a long
  // Endurance run never runs dry mid-flow.
  const out = [];
  while (out.length < count && bag.length) {
    out.push(...shuffle(bag).slice(0, count - out.length));
  }
  return out.slice(0, count);
}

// --- scoring ----------------------------------------------------------------

/**
 * What one correct answer is worth.
 *
 * The streak multiplier is capped at 2x on purpose. Uncapped, a long Endurance
 * run makes the last question worth more than the first eighty put together,
 * and the score stops meaning "how well did I do" and starts meaning "how long
 * did I survive" — which is what the TIME is already for.
 */
export function pointsFor({ difficulty = 'warm-up', streak = 0, aus = false, multiplier = 1 } = {}) {
  const base = BASE_POINTS[difficulty] ?? 100;
  const streakBoost = Math.min(2, 1 + streak * 0.1);
  const points = base * streakBoost * multiplier * (aus ? 2 : 1);
  return Math.round(points / 5) * 5;
}

// --- how Endurance wears him down -------------------------------------------
//
// Faster AND harder, exactly as the brief asks. The clock per question is what
// actually ends the run; the difficulty is what makes the clock feel short.

export const ENDURANCE_STAGES = [
  { untilMs: 45000, perQuestionMs: 9000, floor: 'warm-up', label: 'Warm-up' },
  { untilMs: 105000, perQuestionMs: 7000, floor: 'warm-up', label: 'Getting sweaty' },
  { untilMs: 180000, perQuestionMs: 5500, floor: 'sweaty', label: 'Sweaty' },
  { untilMs: 270000, perQuestionMs: 4500, floor: 'sweaty', label: 'Puffed out' },
  { untilMs: Infinity, perQuestionMs: 3500, floor: 'fully-austinated', label: 'FULLY AUSTINATED' },
];

/**
 * @param {number} elapsedMs
 * @param {string} [chosen]  the rung from settings, so a ceiling can hold
 *
 * Endurance normally ignores the settings rung and climbs anyway — that is the
 * mode. On a ceiling rung it still gets FASTER, which is the actual mechanic,
 * but it stops getting HARDER: a small child on Stretching should run out of
 * speed, not out of vocabulary.
 */
export function enduranceStage(elapsedMs, chosen) {
  const stage = ENDURANCE_STAGES.find((s) => elapsedMs < s.untilMs) ?? ENDURANCE_STAGES.at(-1);
  if (chosen && CEILING_DIFFICULTIES.has(chosen)) return { ...stage, floor: chosen };
  return stage;
}

// --- reading a time back to him ---------------------------------------------

/**
 * "4 minutes 12 seconds" — the words, not "4m 12s".
 *
 * The brief is specific about this and it is not decoration: the Endurance
 * score is a TIME, and spelling it out is what makes it read like a personal
 * best rather than a stopwatch reading.
 */
export function spellDuration(ms) {
  const total = Math.max(0, Math.round(ms / 1000));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  const m = mins === 1 ? '1 minute' : `${mins} minutes`;
  const s = secs === 1 ? '1 second' : `${secs} seconds`;
  if (!mins) return s;
  if (!secs) return m;
  return `${m} ${s}`;
}

/** The compact form, for a row in a list. */
export function shortDuration(ms) {
  const total = Math.max(0, Math.round(ms / 1000));
  return total < 60 ? `${total}s` : `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, '0')}s`;
}
